import { FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import { getFieldWireType, isTokenRiskyField } from "./preselectFieldMatching";

// Unit-separator-keyed identity for an injected (field, value) pair. Keep the
// separator as an escape sequence — a literal U+001F in the source makes git
// treat this file as binary (no diffs, no blame, no merges).
export const injectedKey = (realFieldName: string, value: string): string =>
  `${realFieldName}\u001F${value}`;

// True if any token-risky section has a SELECTED value — i.e. the effective grid
// query filters on a token-risky field. Used (with preselectReconcilePending) to
// derive the grid's shimmer suppression: fetch #1 used raw values for that field
// and may be empty/wrong, so the grid must not paint until reconcile settles.
// The selected-item requirement matters: after reconcile publishes the FULL
// section list (every filterable field — most libraries include User columns like
// Author) there is a commit where pending is still true; only a field the
// querystring actually targeted may keep suppression on in that commit.
export const sectionsHaveTokenRiskySelection = (sections: FilterSection[]): boolean =>
  (sections ?? []).some(section =>
    isTokenRiskyField(section?.filterField) &&
    (section?.items ?? []).some(item => item.selected));

export interface InjectedProbeTarget {
  key: string;
  realFieldName: string;
  type: string;
  value: string;
}

/**
 * Collects every injected (`preselectInjected`) value as a probe target. Each gets
 * a definitive `field = value` existence probe — deliberately no shortcut: probes
 * are RowLimit=1 requests and injected values are rare, so inferring validity from
 * the grid result is not worth the coupling to grid state. Never uses the value
 * list, which is paged/capped on large libraries.
 */
export const collectInjectedProbeTargets = (sections: FilterSection[]): InjectedProbeTarget[] => {
  const targets: InjectedProbeTarget[] = [];

  (sections ?? []).forEach(section => {
    // Datasource-mutated (multiSelect-bound Text) sections are filtered with
    // <Contains> substring CAML, not the FilterField/In exact-match syntax this
    // probe mirrors: an exact probe strikes split parts that live only inside
    // joined cell values ("balans, winst") while the grid's Contains clause is
    // actively matching them. Fail open like the office buckets below — never
    // probed, never struck.
    if (section?.datasourceMutated) {
      return;
    }
    const realFieldName = section?.filterField?.RealFieldName ?? "";
    // Wire-type accessor, not .Type directly: build-loss fallback sections carry
    // schema-origin fields that may expose the type only as FieldType, and a
    // probe sent with an empty FilterType2= is silently inert.
    const type = getFieldWireType(section?.filterField);

    (section?.items ?? [])
      // Skip items carrying `fileType` — office BUCKET injections, whose `value`
      // is a PRE-ENCODED extension list: not a single probe-able filter value
      // (and exempt from encodeFilterValue in the grid builder, which the probe
      // must mirror). Those fail open: never probed, never struck. Raw DocIcon
      // injections deliberately carry NO fileType, so they stay probe-able like
      // any other injected value.
      .filter(item => item.preselectInjected && !item.fileType)
      .forEach(item => {
        const value = item.value ?? "";
        targets.push({ key: injectedKey(realFieldName, value), realFieldName, type, value });
      });
  });

  return targets;
};

/**
 * Applies strike decisions (key → `unavailable` value) to injected items, leaving
 * every other item (and unchanged injected items) referentially identical.
 * `changed` is true iff any `unavailable` flag actually moved — the caller uses it
 * to skip a no-op dispatch and avoid a render loop.
 */
export const applyInjectedStrike = (
  sections: FilterSection[],
  decisions: Map<string, boolean>,
): { sections: FilterSection[]; changed: boolean } => {
  let changed = false;

  const nextSections = (sections ?? []).map(section => {
    const realFieldName = section?.filterField?.RealFieldName ?? "";
    let sectionChanged = false;

    const items = (section?.items ?? []).map(item => {
      if (!item.preselectInjected) {
        return item;
      }
      const next = decisions.get(injectedKey(realFieldName, item.value ?? ""));
      if (next === undefined || item.unavailable === next) {
        return item;
      }
      sectionChanged = true;
      changed = true;
      return { ...item, unavailable: next };
    });

    return sectionChanged ? { ...section, items } : section;
  });

  return { sections: changed ? nextSections : sections, changed };
};
